import React, {
  useState,
  useRef,
  useImperativeHandle,
  type RefObject,
} from 'react';
import { ApplicationView } from '@aha-app/mvc';
import {
  AhaEditorV2,
  AllPlugins,
  AhaEditorUtils,
  MarkdownSerializer,
  slate,
  type EditorPluginList,
  type AhaEditorV2Controller,
  type DocumentValue,
  type ReferenceAhaObject,
  type EditorPlugin,
} from '@aha-app/aha-editor-v2';
import ReferenceAhaPlugin from 'components/aha_editor_field_v2/plugins/ReferenceAha/ReferenceAhaPlugin';
import PasteReferenceLinkPlugin from 'components/aha_editor_field_v2/plugins/ReferenceAha/PasteReferenceLinkPlugin';
import BatchedRecordLoader from 'components/framework/BatchedRecordLoader';
import AssistantContext, {
  getReportReferenceFromUrl,
  getArtifactReferenceFromUrl,
} from 'javascripts/assistant_context';
import type { AssistantSize } from '../AssistantView/AssistantViewController';

interface EditorApi {
  // Focus the editor.
  focus: () => void;
  // Append content to the end of the editor.
  appendContent: (content: string) => void;
  // Replace the entire editor content with the provided content.
  replaceContent: (content: string) => void;
  // Clear the editor content.
  clearContent: () => void;
}

interface PromptEditorProps {
  initialValue?: string;
  setPrompt: (editedPrompt: string) => void;
  uploadFiles?: (files: FileList) => void;
  disabled?: boolean;
  editorApi: RefObject<EditorApi>;
  size: AssistantSize;
}

const editorToMarkdown = (editor: slate.Editor): string => {
  const serializer = new MarkdownSerializer(editor, {});
  return serializer.serialize();
};

// Display an Aha! Editor for enter prompts and outputing markdown.
const PromptInput: React.FC<PromptEditorProps> = ({
  initialValue,
  setPrompt,
  uploadFiles,
  disabled,
  editorApi,
  size,
}) => {
  const editorController = useRef<AhaEditorV2Controller>(null);
  const [value] = useState<DocumentValue>((): DocumentValue => {
    // TODO: need to handle converting markdown to slate value here.
    if (initialValue && initialValue.length > 0) {
      return {
        children: [
          {
            type: 'paragraph',
            children: [{ text: initialValue }],
          },
        ],
        metadata: { annotations: {} },
      };
    } else {
      return AhaEditorUtils.Editor.emptyDocument();
    }
  });

  useImperativeHandle(
    editorApi,
    (): EditorApi => {
      // We are returning an object that implements the EditorApi interface.
      return {
        focus() {
          const editor = editorController.current?.ahaEditor?.editor;

          if (editor) {
            AhaEditorUtils.Editor.focus(editor);
          }
        },
        appendContent(content: string) {
          const editor = editorController.current?.ahaEditor?.editor;

          if (editor) {
            slate.Transforms.select(editor, {
              anchor: slate.Editor.end(editor, []),
              focus: slate.Editor.end(editor, []),
            });
            slate.Transforms.insertText(editor, content);
          }
        },
        replaceContent(content: string) {
          const editor = editorController.current?.ahaEditor?.editor;

          if (editor) {
            AhaEditorUtils.Selection.selectAll(editor);
            slate.Transforms.insertText(editor, content);
          }
        },
        clearContent() {
          const editor = editorController.current?.ahaEditor?.editor;

          if (editor) {
            AhaEditorUtils.Transforms.deleteAll(editor);
          }
        },
      };
    },
    []
  );

  // Plugin that handles attachments pasted into the editor by uploading them
  // to the chat.
  const PasteAttachmentPlugin: EditorPlugin = {
    name: 'Page attachment plugin',
    insertData: (editor, data, controller) => {
      if (uploadFiles) uploadFiles(data.files);
      return false;
    },
  };

  const PasteReportLinkPlugin: EditorPlugin = {
    name: 'Paste report link plugin',
    insertData: (editor, data, controller) => {
      const text = data.getData('text/plain');
      if (!text) return false;

      const reportRef = getReportReferenceFromUrl(text);
      if (!reportRef) return false;

      AssistantContext.instance.addManualReference(reportRef);

      return true;
    },
  };

  const PasteArtifactLinkPlugin: EditorPlugin = {
    name: 'Paste artifact link plugin',
    insertData: (editor, data, controller) => {
      const text = data.getData('text/plain');
      if (!text) return false;

      const artifactRef = getArtifactReferenceFromUrl(text);
      if (!artifactRef) return false;

      AssistantContext.instance.addManualReference(artifactRef);

      return true;
    },
  };

  const [plugins] = useState<EditorPluginList>(() => [
    ReferenceAhaPlugin({
      onInsertReference: (reference: ReferenceAhaObject) => {
        console.log('Add to scope', reference);
      },
      isModifiable: () => false,
      batchedRecordLoader: new BatchedRecordLoader(),
    }),
    AllPlugins.InlinePlugin,
    AllPlugins.ReferenceRecordSchemaPlugin,
    PasteReportLinkPlugin,
    PasteArtifactLinkPlugin,
    PasteReferenceLinkPlugin,
    AllPlugins.PasteFragmentPlugin,
    AllPlugins.PasteMarkdownPlugin,
    AllPlugins.HotkeysPlugin,
    AllPlugins.MarkdownPlugin,
    AllPlugins.TranscriptPlugin,
    AllPlugins.HideInlinePlugin,
    AllPlugins.HeadersPlugin,
    AllPlugins.BoldPlugin,
    AllPlugins.ItalicPlugin,
    AllPlugins.ListPlugin,
    AllPlugins.CodeSnippetPlugin,
    AllPlugins.CodeBlockPlugin,
    PasteAttachmentPlugin,
  ]);

  return (
    <div
      className='aha-editor-field-v2'
      style={{
        '--aha-editor-v2--content-padding': 'var(--spacing-2)',
        ...(size === 'toolbar' && {
          '--default-type-margin': '0px',
          '--aha-editor--block-margin': '0px',
        }),
      }}
    >
      <AhaEditorV2
        controllerRef={editorController}
        value={value}
        onChange={(value, editor) => {
          setPrompt(editorToMarkdown(editor));
        }}
        plugins={plugins}
        readOnly={disabled}
        behaviors={{
          placeholder: 'Ask the assistant',
          minimumLines: size === 'toolbar' ? 1 : 2,
        }}
        serializerOptions={{ alwaysAcceptMarkdown: true }}
      />
    </div>
  );
};

export default ApplicationView(PromptInput);
export { EditorApi };
